/**
 * "Einstellungen"-Tab im Mandanten-Kontrollzentrum (Admin-Bereich V2 P4,
 * docs/KONZEPT-ADMIN-BEREICH.md § "Mandanten-Detail, 8 Tabs" Punkt 3 + § "Feld-Kategorie" Tabelle
 * Zeile "Betriebsschalter ... Admin darf editieren"; docs/LOOP-ADMIN-BEREICH.md P4, fuenftes und
 * letztes substanzielles Kriterium vor dem Mini-Gate).
 *
 * Backend: `GET/PATCH /api/admin/tenants/:id/settings` (Migration 378,
 * `apps/api/src/routes/admin.ts`). Editierbar ist AUSSCHLIESSLICH die dortige
 * `TENANT_SETTINGS_ADMIN_WHITELIST` (25 Felder) — jedes andere Feld aus der GET-Antwort
 * (Zahlungskonditionen, Bank, Branding, ...) wird hier bewusst NICHT gerendert, auch nicht
 * read-only (laut Konzept-Tabelle ein eigenes, spaeteres Kriterium). `acceptanceFlow`/
 * `paymentPathway` sind seit dem Mini-Gate (docs/LOOP-ADMIN-BEREICH.md P4, Antoines Freigabe
 * 13.10.2026) editierbar, Abschnitt "Annahme & Zahlung"; Werte wie in routes/settings.ts.
 *
 * `EDITABLE_KEYS`/`EditableTenantSettings` sind inhaltlich (Feldmenge + Typen) wortgleich mit
 * `TENANT_SETTINGS_ADMIN_WHITELIST`/`patchTenantSettingsSchema` in `apps/api/src/routes/admin.ts`
 * — 1:1 uebernommen, nicht geraten. `apps/web` kann `apps/api` nicht importieren (getrennte
 * Deployables, siehe Kopfkommentar `mandant-neu.tsx`), deshalb die Duplizierung. Reihenfolge HIER
 * ist fachlich gruppiert (Module/Benachrichtigungen/Betrieb/Firmenkopf/Business-Profil), nicht
 * alphabetisch wie in der Backend-Konstante — die Ratsche fuer TS-Whitelist == SQL-Whitelist
 * vergleicht ohnehin nur die SORTIERTE Menge (`admin-tenant-settings-whitelist-ratsche.test.ts`).
 *
 * Speichern: EIN Formular, EIN Speichern-Knopf. `berechneDiff` schickt NUR die Felder ans Backend,
 * die sich gegenueber dem zuletzt geladenen Stand tatsaechlich geaendert haben — nicht alle 23 auf
 * einmal —, damit `detail.versucht` im Audit-Log schlank bleibt (Vorgabe des vorherigen
 * P4-Kriteriums "Audit-Diff-Test"). Erfolg -> Toast (`useToast()`, P0-Baustein, seit dieser
 * Iteration in `pages/admin/index.tsx` einmal um die ganze Admin-Shell gelegt) + `reload()`, was
 * den lokalen Formular-Stand ueber den `useEffect` unten wieder auf den kanonischen Server-Stand
 * zuruecksetzt. Fehler -> `err.message` inline per `ErrorNote` (Bestandsmuster `saveEdit` in
 * `tenant-detail.tsx`), kein stiller Fehlschlag, keine falsche Erfolgsmeldung.
 *
 * Boolean-Felder: neuer, wiederverwendbarer `Toggle`-Baustein (`ui/components.tsx`) — dieselbe
 * visuelle Sprache wie das modul-interne `ToggleSwitch` in `pages/settings.tsx` (dort nicht
 * exportiert). Enum-Felder (`agentMode`, `mailFreigabeModus`, `industryCategory`): `Select`
 * (P0-Baustein, Radix) — `industryCategory` ist nullbar, Radix' `Select.Item` verbietet aber einen
 * leeren String als `value` (interne Reservierung fuer "keine Auswahl"), deshalb ein eigener
 * Sentinel-Wert `INDUSTRY_KEINE_ANGABE`, der beim Lesen/Schreiben auf `null` abgebildet wird. Alle
 * anderen Textfelder: normale `.input`-Felder, leere Eingabe wird beim Speichern zu `null`
 * (Muster `saveEdit` in `tenant-detail.tsx`: `editStripe.trim() === "" ? null : ...`).
 */
import { useEffect, useState, type ReactNode } from "react";
import { useApi, apiMutate } from "../../../hooks/useApi";
import { EmptyState, ErrorNote, Icon, LoadingRows, Select, Toggle, useToast } from "../../../ui/components";

/** 1:1 aus `TENANT_SETTINGS_ADMIN_WHITELIST` (`apps/api/src/routes/admin.ts`) uebernommen — exakt
 *  diese 25 Feldnamen darf `PATCH .../settings` schreiben. */
const EDITABLE_KEYS = [
  "feiertageEnabled",
  "wetterEnabled",
  "handelsregisterEnabled",
  "googlePlacesEnabled",
  "impressumEnabled",
  "ustidEnabled",
  "benachrichtigungNeueAnfrageEnabled",
  "benachrichtigungMailBounceEnabled",
  "benachrichtigungMailErfolgEnabled",
  "agentMode",
  "mailFreigabeModus",
  "emailSenderName",
  "companyLegalName",
  "companyAddress",
  "companyRegister",
  "companyVatId",
  "companyPhone",
  "companyEmail",
  "companyPhotoUrl",
  "companyTagline",
  "businessCountry",
  "businessRegion",
  "industryCategory",
  "acceptanceFlow",
  "paymentPathway",
] as const;

/** Antwortform, NUR die editierbaren Felder — die GET-Antwort liefert daneben ~40 weitere Spalten
 *  (Zahlungskonditionen, Bank, Branding, ...), die dieser Tab bewusst nie liest oder typisiert. */
interface EditableTenantSettings {
  feiertageEnabled: boolean;
  wetterEnabled: boolean;
  handelsregisterEnabled: boolean;
  googlePlacesEnabled: boolean;
  impressumEnabled: boolean;
  ustidEnabled: boolean;
  benachrichtigungNeueAnfrageEnabled: boolean;
  benachrichtigungMailBounceEnabled: boolean;
  benachrichtigungMailErfolgEnabled: boolean;
  agentMode: "off" | "shadow" | "live";
  mailFreigabeModus: "manuell" | "automatisch";
  emailSenderName: string | null;
  companyLegalName: string | null;
  companyAddress: string | null;
  companyRegister: string | null;
  companyVatId: string | null;
  companyPhone: string | null;
  companyEmail: string | null;
  companyPhotoUrl: string | null;
  companyTagline: string | null;
  businessCountry: string | null;
  businessRegion: string | null;
  industryCategory: "catering" | "event_location" | "restaurant" | "other" | null;
  acceptanceFlow: "explicit" | "direct_payment" | null;
  paymentPathway: "stripe" | "kostenuebernahme" | null;
}

const INDUSTRY_OPTIONEN: { value: string; label: string }[] = [
  { value: "catering", label: "Catering (Außer-Haus-Lieferung)" },
  { value: "event_location", label: "Event-Location (Vermietung/Ausrichtung im eigenen Haus)" },
  { value: "restaurant", label: "Restaurant" },
  { value: "other", label: "Sonstiges" },
];
/** Radix' `Select.Item` verbietet einen leeren String als `value` — eigener Sentinel statt "". */
const INDUSTRY_KEINE_ANGABE = "keine-angabe";

const AGENT_MODE_OPTIONEN = [
  { value: "off", label: "Aus" },
  { value: "shadow", label: "Mitlesend (Entwürfe, keine Zustellung)" },
  { value: "live", label: "Aktiv" },
];
/** `null` = Standard des Systems; Radix-Select braucht dafuer einen nicht-leeren Sentinel. */
const STANDARD = "standard";
const ANNAHME_OPTIONEN = [
  { value: STANDARD, label: "Standard (Angebot annehmen)" },
  { value: "explicit", label: "Angebot annehmen" },
  { value: "direct_payment", label: "Direkt zum Zahlungsschritt" },
];
const ZAHLUNGSWEG_OPTIONEN = [
  { value: STANDARD, label: "Standard (Stripe)" },
  { value: "stripe", label: "Stripe" },
  { value: "kostenuebernahme", label: "Kostenübernahme" },
];
const MAIL_FREIGABE_OPTIONEN = [
  { value: "manuell", label: "Manuell (jede Mail wartet auf Freigabe)" },
  { value: "automatisch", label: "Automatisch" },
];

/** Leere/nur-Leerzeichen-Eingabe wird beim Speichern zu `null` — Muster `saveEdit` in
 *  `tenant-detail.tsx` (`editStripe.trim() === "" ? null : editStripe.trim()`). */
function leerZuNull(wert: string): string | null {
  const getrimmt = wert.trim();
  return getrimmt === "" ? null : getrimmt;
}

/** Nur die Felder, die sich zwischen dem zuletzt geladenen Stand und dem lokalen Formular
 *  tatsaechlich unterscheiden — Grundlage fuer den schlanken PATCH-Body (siehe Kopfkommentar).
 *  `Record<string, unknown>` statt direktem `Partial<EditableTenantSettings>`-Aufbau in der
 *  Schleife: TypeScript kann bei einem generischen Schluessel aus einer Vereinigung von Feldnamen
 *  nicht verifizieren, dass `ziel[schluessel] = quelle[schluessel]` typsicher ist (bekannte
 *  Einschraenkung bei indexierter Zuweisung ueber generische Schluessel) — der einzige Cast steht
 *  hier, einmal, am Rueckgabewert; jedes einzelne Feld bleibt oben vollstaendig durchtypisiert. */
function berechneDiff(
  original: EditableTenantSettings,
  aktuell: EditableTenantSettings,
): Partial<EditableTenantSettings> {
  const diff: Record<string, unknown> = {};
  for (const key of EDITABLE_KEYS) {
    if (original[key] !== aktuell[key]) diff[key] = aktuell[key];
  }
  return diff as Partial<EditableTenantSettings>;
}

function Abschnitt({ titel, children }: { titel: string; children: ReactNode }) {
  return (
    <section className="card overflow-hidden">
      <header className="border-b border-outline-variant/30 px-4 py-3 dark:border-outline/30">
        <h3 className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
          {titel}
        </h3>
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function ToggleZeile({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-outline-variant/40 p-3 dark:border-outline/30">
      <div className="min-w-0 flex-1">
        <p className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">{label}</p>
        <p className="mt-0.5 font-body-sm text-[12px] text-secondary-fixed-dim">{hint}</p>
      </div>
      <Toggle checked={checked} onChange={onChange} label={label} />
    </div>
  );
}

function EditFeld({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
        {label}
      </span>
      {children}
    </label>
  );
}

export function EinstellungenTab({ tenantId }: { tenantId: string }) {
  const { data, loading, error, reload } = useApi<EditableTenantSettings>(`/api/admin/tenants/${tenantId}/settings`);
  const [form, setForm] = useState<EditableTenantSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const toast = useToast();

  // Bei jedem frischen Laden (Erstladen ODER `reload()` nach erfolgreichem Speichern) den
  // lokalen Formular-Stand auf den kanonischen Server-Stand zuruecksetzen. `data` aendert sich
  // NICHT waehrend der Nutzer tippt (kein Polling hier) - das Formular bleibt also unangetastet,
  // solange kein neuer Server-Stand ankommt.
  useEffect(() => {
    if (data) setForm({ ...data });
  }, [data]);

  function setField<K extends keyof EditableTenantSettings>(key: K, value: EditableTenantSettings[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }

  async function speichern() {
    if (!data || !form) return;
    const diff = berechneDiff(data, form);
    if (Object.keys(diff).length === 0) return;
    setBusy(true);
    setSaveError(null);
    try {
      await apiMutate(`/api/admin/tenants/${tenantId}/settings`, "PATCH", diff);
      toast.success("Einstellungen gespeichert.");
      reload();
    } catch (err) {
      // Kein stiller Fehlschlag, keine falsche Erfolgsmeldung - der Fehlertext des Servers ist
      // bereits ein fertiger deutscher Satz (Muster `saveEdit` in `tenant-detail.tsx`).
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (loading && !form) {
    return (
      <div className="card mt-4 overflow-hidden">
        <LoadingRows rows={5} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="card mt-4 overflow-hidden">
        <ErrorNote message={error} onRetry={reload} />
      </div>
    );
  }
  if (!data || !form) {
    return (
      <div className="card mt-4 overflow-hidden">
        <EmptyState icon="tune" title="Keine Einstellungen gefunden" />
      </div>
    );
  }

  const geaendert = Object.keys(berechneDiff(data, form)).length > 0;

  return (
    <div className="mt-4 space-y-4">
      <Abschnitt titel="Module">
        <div className="space-y-3">
          <ToggleZeile
            label="Feiertage im Kalender"
            hint="Bundesweite und regionale Feiertage als Badge im Kalender."
            checked={form.feiertageEnabled}
            onChange={(v) => setField("feiertageEnabled", v)}
          />
          <ToggleZeile
            label="Wetter je Vorgang"
            hint="Kurze Wetterinfo im Vorgang (Bright Sky/DWD)."
            checked={form.wetterEnabled}
            onChange={(v) => setField("wetterEnabled", v)}
          />
          <ToggleZeile
            label="Handelsregister-Check"
            hint="Automatischer Abruf bei neuen Firmenkunden (Handelsregister.de + Bundesanzeiger)."
            checked={form.handelsregisterEnabled}
            onChange={(v) => setField("handelsregisterEnabled", v)}
          />
          <ToggleZeile
            label="Google-Places-Lookup"
            hint='Manueller „Nachschlagen"-Button im Kunden-Profil.'
            checked={form.googlePlacesEnabled}
            onChange={(v) => setField("googlePlacesEnabled", v)}
          />
          <ToggleZeile
            label="Impressum-Abruf"
            hint="Automatischer Abruf der Impressum-Seite bei neuen Firmenkunden."
            checked={form.impressumEnabled}
            onChange={(v) => setField("impressumEnabled", v)}
          />
          <ToggleZeile
            label="USt-IdNr.-Bestätigung"
            hint="Amtliche Bestätigung von EU-USt-IdNr. beim Bundeszentralamt für Steuern."
            checked={form.ustidEnabled}
            onChange={(v) => setField("ustidEnabled", v)}
          />
        </div>
      </Abschnitt>

      <Abschnitt titel="Benachrichtigungen">
        <div className="space-y-3">
          <ToggleZeile
            label="Neue Anfrage"
            hint="Meldung an die Betriebsadresse, sobald eine neue Anfrage eingeht."
            checked={form.benachrichtigungNeueAnfrageEnabled}
            onChange={(v) => setField("benachrichtigungNeueAnfrageEnabled", v)}
          />
          <ToggleZeile
            label="E-Mail kam zurück (Bounce/Beschwerde)"
            hint="Meldung, wenn eine verschickte Mail zurückkommt oder als Spam markiert wird."
            checked={form.benachrichtigungMailBounceEnabled}
            onChange={(v) => setField("benachrichtigungMailBounceEnabled", v)}
          />
          <ToggleZeile
            label="E-Mail wurde zugestellt"
            hint="Bestätigung pro erfolgreich zugestellter Mail (standardmäßig aus, viele Mails im Alltag)."
            checked={form.benachrichtigungMailErfolgEnabled}
            onChange={(v) => setField("benachrichtigungMailErfolgEnabled", v)}
          />
        </div>
      </Abschnitt>

      <Abschnitt titel="Betrieb">
        <div className="grid gap-4 sm:grid-cols-2">
          <EditFeld label="KI-Assistent-Modus">
            <Select
              ariaLabel="KI-Assistent-Modus"
              value={form.agentMode}
              onValueChange={(v) => setField("agentMode", v as EditableTenantSettings["agentMode"])}
              options={AGENT_MODE_OPTIONEN}
            />
          </EditFeld>
          <EditFeld label="Mail-Freigabe">
            <Select
              ariaLabel="Mail-Freigabe"
              value={form.mailFreigabeModus}
              onValueChange={(v) => setField("mailFreigabeModus", v as EditableTenantSettings["mailFreigabeModus"])}
              options={MAIL_FREIGABE_OPTIONEN}
            />
          </EditFeld>
          <EditFeld label="Absendername">
            <input
              type="text"
              className="input"
              value={form.emailSenderName ?? ""}
              onChange={(e) => setField("emailSenderName", leerZuNull(e.target.value))}
              placeholder="Musterhof Catering"
            />
          </EditFeld>
        </div>
      </Abschnitt>

      <Abschnitt titel="Annahme & Zahlung">
        <div className="grid gap-4 sm:grid-cols-2">
          <EditFeld label="Annahme-Weg des Gasts">
            <Select
              ariaLabel="Annahme-Weg des Gasts"
              value={form.acceptanceFlow ?? STANDARD}
              onValueChange={(v) =>
                setField("acceptanceFlow", v === STANDARD ? null : (v as EditableTenantSettings["acceptanceFlow"]))
              }
              options={ANNAHME_OPTIONEN}
            />
          </EditFeld>
          <EditFeld label="Zahlungsweg nach der Annahme">
            <Select
              ariaLabel="Zahlungsweg nach der Annahme"
              value={form.paymentPathway ?? STANDARD}
              onValueChange={(v) =>
                setField("paymentPathway", v === STANDARD ? null : (v as EditableTenantSettings["paymentPathway"]))
              }
              options={ZAHLUNGSWEG_OPTIONEN}
            />
          </EditFeld>
        </div>
        <p className="mt-3 flex items-start gap-1.5 font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">
          <Icon name="info" className="text-[16px]" />
          Gilt als Vorgabe des Betriebs. Einzelne Angebote können eigene Werte haben.
        </p>
      </Abschnitt>

      <Abschnitt titel="Firmenkopf">
        <div className="grid gap-4 sm:grid-cols-2">
          <EditFeld label="Firmenname (rechtlich)">
            <input
              type="text"
              className="input"
              value={form.companyLegalName ?? ""}
              onChange={(e) => setField("companyLegalName", leerZuNull(e.target.value))}
              placeholder="Musterhof Catering GmbH"
            />
          </EditFeld>
          <EditFeld label="Adresse">
            <input
              type="text"
              className="input"
              value={form.companyAddress ?? ""}
              onChange={(e) => setField("companyAddress", leerZuNull(e.target.value))}
              placeholder="Musterstraße 12 · 10115 Berlin"
            />
          </EditFeld>
          <EditFeld label="Registereintrag">
            <input
              type="text"
              className="input"
              value={form.companyRegister ?? ""}
              onChange={(e) => setField("companyRegister", leerZuNull(e.target.value))}
              placeholder="Amtsgericht Berlin HRB 123456"
            />
          </EditFeld>
          <EditFeld label="USt-IdNr.">
            <input
              type="text"
              className="input font-mono"
              value={form.companyVatId ?? ""}
              onChange={(e) => setField("companyVatId", leerZuNull(e.target.value))}
              placeholder="DE123456789"
            />
          </EditFeld>
          <EditFeld label="Telefon">
            <input
              type="text"
              className="input"
              value={form.companyPhone ?? ""}
              onChange={(e) => setField("companyPhone", leerZuNull(e.target.value))}
              placeholder="+49 30 12345678"
            />
          </EditFeld>
          <EditFeld label="E-Mail">
            <input
              type="text"
              className="input"
              value={form.companyEmail ?? ""}
              onChange={(e) => setField("companyEmail", leerZuNull(e.target.value))}
              placeholder="info@musterhof-catering.de"
            />
          </EditFeld>
          <EditFeld label="Foto-URL (Hero-Band in der Angebots-Mail)">
            <input
              type="text"
              className="input font-mono"
              value={form.companyPhotoUrl ?? ""}
              onChange={(e) => setField("companyPhotoUrl", leerZuNull(e.target.value))}
              placeholder="https://…/foto.jpg"
            />
          </EditFeld>
          <EditFeld label="Tagline">
            <input
              type="text"
              className="input"
              value={form.companyTagline ?? ""}
              onChange={(e) => setField("companyTagline", leerZuNull(e.target.value))}
              placeholder="Events & Catering · Berlin"
            />
          </EditFeld>
        </div>
      </Abschnitt>

      <Abschnitt titel="Business-Profil">
        <div className="grid gap-4 sm:grid-cols-2">
          <EditFeld label="Land (ISO-Ländercode)">
            <input
              type="text"
              className="input font-mono uppercase"
              maxLength={2}
              value={form.businessCountry ?? ""}
              onChange={(e) => setField("businessCountry", leerZuNull(e.target.value.toUpperCase()))}
              placeholder="DE"
            />
          </EditFeld>
          <EditFeld label="Wirtschaftsraum / Region">
            <input
              type="text"
              className="input"
              value={form.businessRegion ?? ""}
              onChange={(e) => setField("businessRegion", leerZuNull(e.target.value))}
              placeholder="z. B. Berlin/Brandenburg"
            />
          </EditFeld>
          <EditFeld label="Branche">
            <Select
              ariaLabel="Branche"
              value={form.industryCategory ?? INDUSTRY_KEINE_ANGABE}
              onValueChange={(v) =>
                setField(
                  "industryCategory",
                  v === INDUSTRY_KEINE_ANGABE ? null : (v as EditableTenantSettings["industryCategory"]),
                )
              }
              options={[{ value: INDUSTRY_KEINE_ANGABE, label: "Nicht angegeben" }, ...INDUSTRY_OPTIONEN]}
            />
          </EditFeld>
        </div>
      </Abschnitt>

      {saveError && <ErrorNote message={saveError} />}
      <div className="flex justify-end">
        <button type="button" className="btn-primary" disabled={busy || !geaendert} onClick={() => void speichern()}>
          {busy ? "Speichert …" : "Speichern"}
        </button>
      </div>
    </div>
  );
}
